import { Head, router, useForm } from "@inertiajs/react"
import { Plus, Trash2 } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import type PlayerSeasonsIndex from "@/types/serializers/PlayerSeasonsIndex"
import type RequestsCreatePlayerSeason from "@/types/serializers/RequestsCreatePlayerSeason"

export default function SeasonsIndex({ seasons }: PlayerSeasonsIndex) {
  const [showForm, setShowForm] = useState(false)

  const { data, setData, post, processing, errors, reset } =
    useForm<RequestsCreatePlayerSeason>({
      player_season: {
        season_name: "",
        year: new Date().getFullYear(),
        club_name: "",
        division: "",
        matches_played: 0,
        goals: 0,
        assists: 0,
        yellow_cards: 0,
        red_cards: 0,
        minutes_played: 0,
      },
    })

  const setField = (
    field: keyof typeof data.player_season,
    value: string | number,
  ) => setData("player_season", { ...data.player_season, [field]: value })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post("/perfil/temporadas", {
      onSuccess: () => {
        reset()
        setShowForm(false)
      },
    })
  }

  function handleDelete(seasonId: number) {
    router.delete(`/perfil/temporadas/${seasonId}`)
  }

  return (
    <AppLayout>
      <Head title="Mis Temporadas | KrownFT" />

      <div className="mx-auto max-w-3xl px-6 py-8">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              Mis Temporadas
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Agregá tus estadísticas de cada temporada
            </p>
          </div>
          <Button
            variant="accent"
            size="sm"
            className="w-full sm:w-auto"
            onClick={() => setShowForm(!showForm)}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Agregar temporada
          </Button>
        </div>

        {showForm && (
          <form
            onSubmit={handleSubmit}
            className="bg-card mb-8 rounded-lg border p-6 shadow-sm"
          >
            <h2 className="mb-4 text-lg font-semibold">Nueva temporada</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="season_name">Nombre de temporada</Label>
                <Input
                  id="season_name"
                  value={data.player_season.season_name}
                  onChange={(e) => setField("season_name", e.target.value)}
                  placeholder="Ej: Apertura 2025"
                />
                {errors["player_season.season_name"] && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors["player_season.season_name"]}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="year">Año</Label>
                <Input
                  id="year"
                  type="number"
                  value={String(data.player_season.year)}
                  onChange={(e) => setField("year", Number(e.target.value))}
                />
                {errors["player_season.year"] && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors["player_season.year"]}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="club_name">Club</Label>
                <Input
                  id="club_name"
                  value={data.player_season.club_name}
                  onChange={(e) => setField("club_name", e.target.value)}
                  placeholder="Ej: Rosario Central"
                />
              </div>
              <div>
                <Label htmlFor="division">División</Label>
                <Input
                  id="division"
                  value={data.player_season.division}
                  onChange={(e) => setField("division", e.target.value)}
                  placeholder="Ej: Primera División"
                />
              </div>
              <div>
                <Label htmlFor="matches_played">Partidos jugados</Label>
                <Input
                  id="matches_played"
                  type="number"
                  value={String(data.player_season.matches_played)}
                  onChange={(e) =>
                    setField("matches_played", Number(e.target.value))
                  }
                />
              </div>
              <div>
                <Label htmlFor="goals">Goles</Label>
                <Input
                  id="goals"
                  type="number"
                  value={String(data.player_season.goals)}
                  onChange={(e) => setField("goals", Number(e.target.value))}
                />
              </div>
              <div>
                <Label htmlFor="assists">Asistencias</Label>
                <Input
                  id="assists"
                  type="number"
                  value={String(data.player_season.assists)}
                  onChange={(e) => setField("assists", Number(e.target.value))}
                />
              </div>
              <div>
                <Label htmlFor="yellow_cards">Tarjetas amarillas</Label>
                <Input
                  id="yellow_cards"
                  type="number"
                  value={String(data.player_season.yellow_cards)}
                  onChange={(e) =>
                    setField("yellow_cards", Number(e.target.value))
                  }
                />
              </div>
              <div>
                <Label htmlFor="red_cards">Tarjetas rojas</Label>
                <Input
                  id="red_cards"
                  type="number"
                  value={String(data.player_season.red_cards)}
                  onChange={(e) =>
                    setField("red_cards", Number(e.target.value))
                  }
                />
              </div>
              <div>
                <Label htmlFor="minutes_played">Minutos jugados</Label>
                <Input
                  id="minutes_played"
                  type="number"
                  value={String(data.player_season.minutes_played)}
                  onChange={(e) =>
                    setField("minutes_played", Number(e.target.value))
                  }
                />
              </div>
            </div>
            <div className="mt-6 flex gap-2">
              <Button type="submit" disabled={processing}>
                Guardar
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForm(false)}
              >
                Cancelar
              </Button>
            </div>
          </form>
        )}

        {seasons.length === 0 ? (
          <p className="text-muted-foreground py-12 text-center">
            Aún no agregaste temporadas
          </p>
        ) : (
          <div className="space-y-4">
            {seasons.map((season) => (
              <div
                key={season.id}
                className="bg-card flex items-center justify-between rounded-lg border p-4 shadow-sm"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold">{season.season_name}</h3>
                    <span className="text-muted-foreground text-sm">
                      {season.club_name}
                    </span>
                  </div>
                  <div className="text-muted-foreground mt-1 flex gap-4 text-sm">
                    <span>PJ: {season.matches_played}</span>
                    <span>Goles: {season.goals}</span>
                    <span>Asist: {season.assists}</span>
                    <span>TA: {season.yellow_cards}</span>
                    <span>TR: {season.red_cards}</span>
                    <span>Min: {season.minutes_played}</span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(season.id)}
                  aria-label="Eliminar"
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
